import { useEffect, useRef, useState } from 'react';
import {
  Broadcast,
  FilmSlate,
  InstagramLogo,
  MetaLogo,
  SlackLogo,
  Sparkle,
  TiktokLogo,
} from '@phosphor-icons/react';
import { FACTORY_COPY, FACTORY_FLOW_NODES } from './landingContent';

const BUILD_MS = 300;
const RUN_MS = 1900;
const HOLD_MS = 2400;
const TOTAL = FACTORY_FLOW_NODES.length;

/**
 * Canvas coordinate space. Nodes are placed as a percentage of these
 * numbers and the edge curves are computed in the same space, so the
 * whole graph scales with the container without measuring the DOM.
 */
const CANVAS_W = 1000;
const CANVAS_H = 300;
const NODE_X = [76, 217, 358, 500, 642, 783, 924];
const NODE_Y = [104, 196, 104, 196, 104, 196, 104];
const PORT_GAP = 34;

const ICONS = {
  instagram: InstagramLogo,
  tiktok: TiktokLogo,
  'meta-ads': MetaLogo,
  ideate: Sparkle,
  generate: FilmSlate,
  approve: SlackLogo,
  publish: Broadcast,
};

/** Bezier between two node ports, in canvas units. */
function edgePath(from, to) {
  const x1 = NODE_X[from] + PORT_GAP;
  const y1 = NODE_Y[from];
  const x2 = NODE_X[to] - PORT_GAP;
  const y2 = NODE_Y[to];
  const bend = Math.max(40, (x2 - x1) * 0.55);
  return `M ${x1} ${y1} C ${x1 + bend} ${y1}, ${x2 - bend} ${y2}, ${x2} ${y2}`;
}

/**
 * Per-node micro animation, played inside the node tile while that node
 * runs. Each is a tiny abstraction of the work the node does, so a
 * running node shows its own activity instead of a shared spinner.
 */
function NodeViz({ id }) {
  if (id === 'instagram') {
    return (
      <span className="lp-flow__viz lp-flow__viz--grid">
        {[0, 1, 2, 3, 4, 5].map((tile) => (
          <span key={tile} style={{ animationDelay: `${tile * 150}ms` }} />
        ))}
      </span>
    );
  }
  if (id === 'tiktok') {
    return (
      <span className="lp-flow__viz lp-flow__viz--feed">
        <span className="lp-flow__feed-track">
          {[0, 1, 2, 3].map((card) => (
            <span key={card} />
          ))}
        </span>
      </span>
    );
  }
  if (id === 'meta-ads') {
    return (
      <span className="lp-flow__viz lp-flow__viz--bars">
        {[46, 74, 34, 88].map((height, bar) => (
          <span
            key={height}
            style={{ '--lp-bar-h': `${height}%`, animationDelay: `${bar * 150}ms` }}
          />
        ))}
      </span>
    );
  }
  if (id === 'ideate') {
    return (
      <span className="lp-flow__viz lp-flow__viz--lines">
        {[100, 72, 88].map((width, line) => (
          <span
            key={width}
            style={{ '--lp-line-w': `${width}%`, animationDelay: `${line * 270}ms` }}
          />
        ))}
      </span>
    );
  }
  if (id === 'generate') {
    return (
      <span className="lp-flow__viz lp-flow__viz--frames">
        {[0, 1, 2].map((frame) => (
          <span key={frame} style={{ animationDelay: `${frame * 300}ms` }} />
        ))}
      </span>
    );
  }
  if (id === 'approve') {
    return (
      <span className="lp-flow__viz lp-flow__viz--approve">
        <span className="lp-flow__bubble" />
        <span className="lp-flow__bubble" />
        <span className="lp-flow__tick">
          <svg viewBox="0 0 12 12" aria-hidden="true">
            <path d="M2.5 6.4 L4.8 8.7 L9.5 3.6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </span>
    );
  }
  return (
    <span className="lp-flow__viz lp-flow__viz--publish">
      <span className="lp-flow__pulse" />
      <span className="lp-flow__pulse" />
      <span className="lp-flow__live" />
    </span>
  );
}

/**
 * The workflow assembles itself node by node with its connections, then
 * runs end to end and holds on the finished pass before looping. One
 * `phase` + `cursor` pair drives every node, edge, and micro animation.
 * Runs only while on screen; reduced motion renders the completed run.
 */
function FactoryWorkflow({ reducedMotion }) {
  const [phase, setPhase] = useState(reducedMotion ? 'done' : 'build');
  const [cursor, setCursor] = useState(reducedMotion ? TOTAL : -1);
  const [playing, setPlaying] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (reducedMotion) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setPlaying(entry.isIntersecting),
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reducedMotion]);

  useEffect(() => {
    if (reducedMotion || !playing) return undefined;

    const delay = phase === 'build' ? BUILD_MS : phase === 'run' ? RUN_MS : HOLD_MS;
    const timer = window.setTimeout(() => {
      if (phase === 'build') {
        if (cursor < TOTAL - 1) setCursor(cursor + 1);
        else {
          setPhase('run');
          setCursor(0);
        }
        return;
      }
      if (phase === 'run') {
        if (cursor < TOTAL - 1) setCursor(cursor + 1);
        else setPhase('done');
        return;
      }
      setPhase('build');
      setCursor(-1);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [reducedMotion, playing, phase, cursor]);

  const nodeState = (index) => {
    if (phase === 'done') return 'done';
    if (phase === 'build') return index <= cursor ? 'idle' : 'hidden';
    if (index < cursor) return 'done';
    if (index === cursor) return 'running';
    return 'idle';
  };

  // An edge is drawn once its source node exists, and flows while the
  // run is passing through it.
  const edgeState = (index) => {
    if (phase === 'done') return 'drawn';
    if (phase === 'build') return index < cursor ? 'drawn' : 'hidden';
    return index === cursor ? 'flowing' : 'drawn';
  };

  return (
    <div
      ref={containerRef}
      className={`lp-flow lp-flow--${phase}`}
      role="img"
      aria-label={FACTORY_COPY.flowAlt}
    >
      <div className="lp-flow__canvas">
        <svg
          className="lp-flow__edges"
          viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {FACTORY_FLOW_NODES.slice(0, -1).map((node, index) => (
            <path
              key={node.id}
              className={`lp-flow__edge lp-flow__edge--${edgeState(index)}`}
              d={edgePath(index, index + 1)}
              pathLength="1"
            />
          ))}
        </svg>

        <ol className="lp-flow__nodes">
          {FACTORY_FLOW_NODES.map((node, index) => {
            const Icon = ICONS[node.id];
            const state = nodeState(index);
            return (
              <li
                key={node.id}
                className={`lp-flow__node lp-flow__node--${node.id} lp-flow__node--${state}`}
                // Only the canvas layout consumes these; the stacked
                // mobile layout ignores them.
                style={{
                  '--lp-node-x': `${(NODE_X[index] / CANVAS_W) * 100}%`,
                  '--lp-node-y': `${(NODE_Y[index] / CANVAS_H) * 100}%`,
                }}
              >
                <span className="lp-flow__tile">
                  <span className="lp-flow__port lp-flow__port--in" aria-hidden="true" />
                  <Icon size={26} weight="duotone" aria-hidden="true" className="lp-flow__icon" />
                  <NodeViz id={node.id} />
                  <span className="lp-flow__badge" aria-hidden="true">
                    <svg viewBox="0 0 12 12">
                      <path d="M2.5 6.4 L4.8 8.7 L9.5 3.6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className="lp-flow__port lp-flow__port--out" aria-hidden="true" />
                </span>
                <span className="lp-flow__label">
                  <span className="lp-flow__title">{node.title}</span>
                  <span className="lp-flow__meta">{node.meta}</span>
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

export default FactoryWorkflow;
